import { cn } from './cn';

/** Right-pointing chevron; rotate with a class for expand/collapse or dropdowns. */
export const Chevron = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true" className={className}>
    <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Right-pointing arrow (shaft + head) — the "→" in a "Move to …" affordance. */
export const ArrowRight = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true" className={className}>
    <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Priority mark: a dash (low) or 1–3 stacked up-chevrons (medium / high / blocker),
 * tinted by `color`. `level` 0 = low … 3 = blocker. The stacks are vertically centred
 * in the 16×16 box so the mark stays balanced at every level. Decorative — give it a
 * `title`/`aria-label` on the wrapping element.
 */
export const PriorityChevron = ({ level, color, className }: { level: number; color?: string; className?: string }) => {
  const stroke = color ?? 'currentColor';
  if (level <= 0) {
    // Low — an en-dash: no chevron, just a subtle bar.
    return (
      <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true" className={className}>
        <path d="M4.5 8h7" stroke={stroke} strokeWidth="1.9" strokeLinecap="round" />
      </svg>
    );
  }
  const count = Math.min(level, 3);
  // Baselines (arm y) per count, chosen so each stack centres on y=8 (rise 3.6, gap 4).
  const bases: Record<number, number[]> = { 1: [9.8], 2: [11.8, 7.8], 3: [13.8, 9.8, 5.8] };
  const rise = 3.6;
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true" className={className}>
      {bases[count].map((y, i) => (
        <path
          key={i}
          d={`M3.6 ${y} L8 ${y - rise} L12.4 ${y}`}
          stroke={stroke}
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
};

/**
 * Task-type badge: a rounded, colour-filled square with a white glyph per type (JIRA-style) —
 * Initiative (rising bars), Epic (bolt), Story (bookmark), Task (check), Bug (body + antennae).
 * `color` comes from taskTypeMeta so both stay in lockstep; unknown types fall back to a dot.
 */
export const TaskTypeGlyph = ({ type, color, size = 14, className }: { type: string; color?: string; size?: number; className?: string }) => {
  const c = color ?? 'currentColor';
  const sym = (() => {
    // Every symbol is normalized to a consistent centred box (~x 4.3–11.7, y 4.2–11.8) so no type reads
    // bigger/smaller than another — the bookmark/bolt used to tower over the check/bug.
    switch (type) {
      case 'initiative':
        return (
          <>
            <rect x="4.3" y="8" width="2.1" height="3.5" rx=".6" fill="#fff" />
            <rect x="6.95" y="6.5" width="2.1" height="5" rx=".6" fill="#fff" />
            <rect x="9.6" y="5" width="2.1" height="6.5" rx=".6" fill="#fff" />
          </>
        );
      case 'epic':
        return <path d="M8.8 4.2 L5 8.7 H7.1 L6.7 11.8 L11 7.1 H8.4 Z" fill="#fff" />;
      case 'story':
        return <path d="M5.7 4.5 h4.6 v7 l-2.3-1.7 L5.7 11.5 Z" fill="#fff" />;
      case 'task':
        return (
          <path d="M4.6 8.2 l2.4 2.5 L11.4 5" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        );
      case 'bug':
        return (
          <>
            <path d="M6.7 5.3 L5.9 4.1 M9.3 5.3 L10.1 4.1" stroke="#fff" strokeWidth="1.1" strokeLinecap="round" />
            <rect x="5.4" y="5.7" width="5.2" height="6" rx="2.6" fill="#fff" />
          </>
        );
      default:
        return <circle cx="8" cy="8" r="3.1" fill="#fff" />;
    }
  })();
  return (
    // shrink-0: it's a fixed-size icon — never let a cramped flex row (e.g. a long title in a narrow
    // column) squeeze it smaller, which made the same type read at different sizes across rows.
    <svg viewBox="0 0 16 16" width={size} height={size} fill="none" aria-hidden="true" className={'shrink-0' + (className ? ' ' + className : '')}>
      <rect x="1" y="1" width="14" height="14" rx="3.5" fill={c} />
      {sym}
    </svg>
  );
};

/** A generic gray "?" glyph — the "no epic / Unassigned" counterpart to the coloured epic glyph. */
export const UnassignedGlyph = ({ size = 14, className }: { size?: number; className?: string }) => (
  <svg viewBox="0 0 16 16" width={size} height={size} fill="none" aria-hidden="true" className={className}>
    <rect x="1" y="1" width="14" height="14" rx="3.5" fill="#a1a1aa" />
    <text x="8" y="11.7" textAnchor="middle" fontSize="10.5" fontWeight="700" fontFamily="ui-sans-serif, system-ui, sans-serif" fill="#fff">?</text>
  </svg>
);

/** Box with an arrow leaving the top-right — "open externally / in a new tab". */
export const ExternalLink = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M9 3h4v4" />
    <path d="M13 3 7.5 8.5" />
    <path d="M12.5 9.5V12a1.5 1.5 0 0 1-1.5 1.5H4A1.5 1.5 0 0 1 2.5 12V5A1.5 1.5 0 0 1 4 3.5h2.5" />
  </svg>
);

/** Chain-link glyph for item links / dependencies. */
export const Link = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M6.5 8.5a2.5 2.5 0 0 0 3.6.28l2-2a2.5 2.5 0 1 0-3.54-3.54l-1.1 1.1" />
    <path d="M9.5 7.5a2.5 2.5 0 0 0-3.6-.28l-2 2a2.5 2.5 0 1 0 3.54 3.54l1.1-1.1" />
  </svg>
);

/** Two offset rounded squares — "detach to a floating dialog" (undock the side panel). */
export const Detach = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <rect x="5" y="5" width="8.5" height="8.5" rx="1.8" />
    <path d="M2.5 9.4V4.3A1.8 1.8 0 0 1 4.3 2.5h5.1" />
  </svg>
);

/** Price/label tag — the Source field token. */
export const Tag = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M2.6 7.4V3.3a.7.7 0 0 1 .7-.7h4.1a1 1 0 0 1 .7.3l5 5a1 1 0 0 1 0 1.4l-4.1 4.1a1 1 0 0 1-1.4 0l-5-5a1 1 0 0 1-.7-.9z" />
    <circle cx="5.4" cy="5.4" r=".95" fill="currentColor" stroke="none" />
  </svg>
);

/** Stacked layers — the epic sidebar toggle. */
export const Layers = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M12 3 3 8l9 5 9-5-9-5z" />
    <path d="M3 13l9 5 9-5" />
    <path d="M3 18l9 5 9-5" opacity=".55" />
  </svg>
);

/** 6-dot drag handle. */
export const Grip = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true" className={className}>
    <circle cx="6" cy="3.5" r="1.35" />
    <circle cx="10" cy="3.5" r="1.35" />
    <circle cx="6" cy="8" r="1.35" />
    <circle cx="10" cy="8" r="1.35" />
    <circle cx="6" cy="12.5" r="1.35" />
    <circle cx="10" cy="12.5" r="1.35" />
  </svg>
);

/** Open-in-panel (right drawer) icon. */
export const OpenPanel = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true" className={className}>
    <rect x="2" y="3" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.3" />
    <line x1="9.5" y1="3.4" x2="9.5" y2="12.6" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

/** Calendar. */
export const Calendar = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <rect x="3" y="4.5" width="18" height="16" rx="2" />
    <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
  </svg>
);

/** Check mark. */
export const Check = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M3 8.5l3.5 3.5 6.5-8" />
  </svg>
);

/** Sun (light theme). */
export const Sun = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" className={className}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

/** Moon (dark theme). */
export const Moon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </svg>
);

/** Plus / add. */
export const Plus = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true" className={className}>
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

/** Kanban board (columns). */
export const KanbanIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" className={className}>
    <rect x="2" y="2.5" width="4.5" height="11" rx="1" />
    <rect x="9.5" y="2.5" width="4.5" height="7" rx="1" />
  </svg>
);

/** Table (rows). */
export const TableIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" className={className}>
    <rect x="2" y="3" width="12" height="10" rx="1.5" />
    <path d="M2 6.4h12M2 9.6h12M6.4 6.4V13" strokeWidth="1.2" />
  </svg>
);

/** Timeline / Gantt (offset horizontal bars). */
export const TimelineIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true" className={className}>
    <rect x="2" y="3" width="8" height="2.6" rx="1.3" />
    <rect x="6" y="6.7" width="8" height="2.6" rx="1.3" />
    <rect x="3.5" y="10.4" width="6.5" height="2.6" rx="1.3" />
  </svg>
);

/** Small down-chevron for select/dropdown triggers. */
export const ChevronDown = ({ className, open }: { className?: string; open?: boolean }) => (
  <svg
    viewBox="0 0 16 16"
    width="13"
    height="13"
    fill="none"
    aria-hidden="true"
    className={cn('transition-transform', open && 'rotate-180', className)}
  >
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Two overlapping cards — duplicate. */
export const Duplicate = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true" className={className}>
    <rect x="5.6" y="5.6" width="8" height="8" rx="1.8" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M10.4 5.6V4.1A1.9 1.9 0 0 0 8.5 2.2H4A1.9 1.9 0 0 0 2.1 4.1v4.5A1.9 1.9 0 0 0 4 10.5h1.6"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Trash can — delete. */
export const Trash = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M2.8 4.4h10.4M6.4 4.4V3.1c0-.4.3-.7.7-.7h1.8c.4 0 .7.3.7.7v1.3" />
    <path d="M4.4 4.4l.5 8c0 .5.4.9.9.9h4.4c.5 0 .9-.4.9-.9l.5-8" />
    <path d="M6.7 7v3.6M9.3 7v3.6" />
  </svg>
);

/** A parent row with an indented child row — add sub-item. */
export const SubItem = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <rect x="2.4" y="2.5" width="11.2" height="3.6" rx="1.1" />
    <path d="M5.2 6.9v3.2c0 .9.7 1.6 1.6 1.6h1" />
    <rect x="8.4" y="9.9" width="5.2" height="3.6" rx="1.1" />
  </svg>
);

/** A curved return arrow — reply to a comment (thread). */
export const Reply = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M6.5 4.5 3 8l3.5 3.5" />
    <path d="M3 8h6.2c2 0 3.3 1.3 3.3 3.2V12" />
  </svg>
);

/** Indented lines with a left-pointing arrow — promote a nested item out to the top level. */
export const Promote = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M13.4 3.2H3" />
    <path d="M13.4 12.8H3" />
    <path d="M13.4 6.4H7.4" />
    <path d="M13.4 9.6H7.4" />
    <path d="M4.6 6.3 2.4 8l2.2 1.7" />
  </svg>
);

/** A bar with an up-arrow beneath it — bring a card to the top of its list. */
export const ToTop = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M3 3h10" />
    <path d="M8 13V6.6" />
    <path d="M5.2 9 8 6.2 10.8 9" />
  </svg>
);

/** A bar with a down-arrow above it — send a card to the bottom of its list. */
export const ToBottom = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M3 13h10" />
    <path d="M8 3v6.4" />
    <path d="M5.2 7 8 9.8 10.8 7" />
  </svg>
);

/** A plus above a bar — insert item above. */
export const InsertAbove = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M8 2v3.4M6.3 3.7h3.4" />
    <rect x="2.6" y="8.3" width="10.8" height="5.1" rx="1.4" />
  </svg>
);

/** A bar above a plus — insert item below. */
export const InsertBelow = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <rect x="2.6" y="2.6" width="10.8" height="5.1" rx="1.4" />
    <path d="M8 10.6V14M6.3 12.3h3.4" />
  </svg>
);

/** Magnifying glass — search. */
/** A bar-chart glyph for the Reports section (three columns of increasing height). */
export const ReportsIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M3 21h18" />
    <rect x="5" y="12" width="3.5" height="6" rx="1" />
    <rect x="10.25" y="8" width="3.5" height="10" rx="1" />
    <rect x="15.5" y="4" width="3.5" height="14" rx="1" />
  </svg>
);

export const Search = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <circle cx="7" cy="7" r="4.25" />
    <path d="M10.4 10.4L14 14" />
  </svg>
);

/** Small close / clear cross. */
export const X = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="13"
    height="13"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M4 4l8 8M12 4l-8 8" />
  </svg>
);

/** Pencil — rename / edit. */
export const Pencil = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M11.2 2.6l2.2 2.2M3 10.4l7.3-7.3a1.3 1.3 0 0 1 1.8 0l.8.8a1.3 1.3 0 0 1 0 1.8L5.6 13 2.5 13.5 3 10.4z" />
  </svg>
);

/** Wiki — an open book (the Wiki area). */
export const Wiki = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M12 6.5C10.5 5 8.3 4.5 4 4.7v12.6c4.3-.2 6.5.3 8 1.7 1.5-1.4 3.7-1.9 8-1.7V4.7c-4.3-.2-6.5.3-8 1.8Z" />
    <path d="M12 6.5v12" />
  </svg>
);

/** Cog / gear — settings. Bootstrap Icons "gear" (native 16×16, so it stays crisp at this size). */
export const Cog = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492M5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0" />
    <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115z" />
  </svg>
);

/** Rounded speech bubble with two text lines — comments / chat. Tail sits bottom-left. */
export const Comment = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M13.5 9.2a1.8 1.8 0 0 1-1.8 1.8H6.2L3 13.5V4.3A1.8 1.8 0 0 1 4.8 2.5h6.9a1.8 1.8 0 0 1 1.8 1.8z" />
    <path d="M5.6 5.7h5.2M5.6 8h3.4" />
  </svg>
);

/** Vertical 3-dot "more actions" menu handle. */
export const MoreVertical = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true" className={className}>
    <circle cx="8" cy="3" r="1.4" />
    <circle cx="8" cy="8" r="1.4" />
    <circle cx="8" cy="13" r="1.4" />
  </svg>
);

/** Two opposed arrows — swap / convert (used for "Set status"). */
export const Swap = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M2.8 5.4h8.4M9 3.2l2.2 2.2L9 7.6" />
    <path d="M13.2 10.6H4.8M7 8.4l-2.2 2.2L7 12.8" />
  </svg>
);

/** Push-pin — the "keep the details panel open as a side drawer" toggle. Filled when pinned. */
export const Pin = ({ className, filled }: { className?: string; filled?: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M9.4 3.5h5.2l-.7 2.4a3 3 0 0 0 .74 2.94l1.86 1.86H7.5l1.86-1.86a3 3 0 0 0 .74-2.94z" />
    <path d="M12 10.7V20" />
  </svg>
);

/** Two-pin plug — the MCP connector. */
export const Plug = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M9 2v5M15 2v5" />
    <path d="M6 7h12v3a6 6 0 0 1-12 0z" />
    <path d="M12 16v6" />
  </svg>
);

/** Down-arrow into a tray — download. */
export const Download = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M12 3v12M7.5 10.5 12 15l4.5-4.5" />
    <path d="M5 20h14" />
  </svg>
);

/** Laptop — the desktop app. */
export const Laptop = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <rect x="4" y="5" width="16" height="11" rx="1.5" />
    <path d="M2.5 19.5h19" />
  </svg>
);

/** Door with an out-arrow — sign out. */
export const SignOut = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M14 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8" />
    <path d="M10 12h11M18 9l3 3-3 3" />
  </svg>
);

/** Two rows collapsing toward a centre line (arrows pointing in) — "collapse all". */
export const CollapseAll = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M4 12h16" />
    <path d="M9 7l3 3 3-3" />
    <path d="M9 17l3-3 3 3" />
  </svg>
);

/** Two rows springing apart from a centre line (arrows pointing out) — "expand all". */
export const ExpandAll = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M4 12h16" />
    <path d="M9 5l3-3 3 3" />
    <path d="M9 19l3 3 3-3" />
  </svg>
);

/** Table with a highlighted right column — the "configure columns" control. */
export const Columns = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M9 4v16M15 4v16" />
  </svg>
);

/** A paint droplet — the "colour this epic" affordance. Fill follows currentColor for a solid dot. */
export const Palette = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M12 3c-4.5 0-8 3.4-8 7.6 0 3.5 2.7 5.4 5 5.4 1.3 0 2-.8 2-1.8 0-.5-.2-.8-.5-1.1-.3-.4-.5-.7-.5-1.2 0-.9.8-1.6 1.7-1.6H15c2.8 0 5-2.1 5-4.8C20 5.9 16.4 3 12 3Z" />
    <circle cx="8" cy="9" r="1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="7" r="1" fill="currentColor" stroke="none" />
    <circle cx="16" cy="9" r="1" fill="currentColor" stroke="none" />
  </svg>
);

/** Two people — the Members / team icon. */
export const Members = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-4A3.5 3.5 0 0 0 5 17.5V19" />
    <circle cx="10.5" cy="8" r="3" />
    <path d="M19 19v-1.4a3.4 3.4 0 0 0-2.6-3.3" />
    <path d="M15.5 5.2a3 3 0 0 1 0 5.6" />
  </svg>
);

/** Bell — the notifications icon. */
export const Bell = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
    <path d="M10.5 20a2 2 0 0 0 3 0" />
  </svg>
);

/** Eye — the watch / following icon. */
export const Eye = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="2.6" />
  </svg>
);

/** Gear/settings cog (a second, rounder cog for the settings area). */
export const Settings = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v2.5M12 19.5V22M4.2 4.2l1.8 1.8M18 18l1.8 1.8M2 12h2.5M19.5 12H22M4.2 19.8 6 18M18 6l1.8-1.8" />
  </svg>
);
